import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  TextField,
  MenuItem,
  IconButton,
  Divider,
  Card,
  CardContent,
  Chip,
  Avatar,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import { Close, Hotel, CalendarToday, Person, Payment } from '@mui/icons-material';
import { differenceInDays, format } from 'date-fns';

const roomTypes = [
  { type: 'Standard', price: 120, capacity: 2, amenities: 'WiFi, TV, AC' },
  { type: 'Deluxe', price: 180, capacity: 2, amenities: 'WiFi, TV, AC, Mini Bar, City View' },
  { type: 'Suite', price: 280, capacity: 4, amenities: 'WiFi, TV, AC, Mini Bar, Kitchen, Ocean View' },
  { type: 'Family', price: 220, capacity: 5, amenities: 'WiFi, TV, AC, 2 Bedrooms, Kitchen' },
];

const steps = ['Select Room', 'Guest Details', 'Confirmation'];

export default function CustomerBookingDialog({ open, onClose, onSave }) {
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    roomType: 'Deluxe',
    checkIn: new Date().toISOString().split('T')[0],
    checkOut: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    guests: 2,
    fullName: '',
    email: '',
    phone: '',
    idNumber: '',
    specialRequests: '',
    paymentMethod: 'Credit Card',
  });

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleNext = () => {
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleSubmit = () => {
    const selectedRoom = roomTypes.find(r => r.type === formData.roomType);
    const nights = differenceInDays(new Date(formData.checkOut), new Date(formData.checkIn));
    const total = selectedRoom.price * nights;

    const bookingData = {
      id: `BK${Math.floor(Math.random() * 10000)}`,
      ...formData,
      roomNumber: `${formData.roomType.substring(0, 3).toUpperCase()}-${Math.floor(Math.random() * 400) + 101}`,
      nights,
      pricePerNight: selectedRoom.price,
      totalAmount: total,
      status: 'Confirmed',
      bookingDate: new Date().toISOString().split('T')[0],
    };

    onSave(bookingData);
    handleClose();
  };

  const handleClose = () => {
    setActiveStep(0);
    setFormData({
      roomType: 'Deluxe',
      checkIn: new Date().toISOString().split('T')[0],
      checkOut: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      guests: 2,
      fullName: '',
      email: '',
      phone: '',
      idNumber: '',
      specialRequests: '',
      paymentMethod: 'Credit Card',
    });
    onClose();
  };

  const selectedRoom = roomTypes.find(r => r.type === formData.roomType);
  const nights = differenceInDays(new Date(formData.checkOut), new Date(formData.checkIn));
  const totalAmount = selectedRoom ? selectedRoom.price * nights : 0;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={2}>
            <Hotel color="primary" />
            <Typography variant="h6">New Booking</Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {activeStep === 0 && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Select Room Type
              </Typography>
            </Grid>
            {roomTypes.map((room) => (
              <Grid item xs={12} sm={6} key={room.type}>
                <Card
                  sx={{
                    cursor: 'pointer',
                    border: formData.roomType === room.type ? '2px solid #1976d2' : '2px solid transparent',
                    '&:hover': { boxShadow: 3 },
                  }}
                  onClick={() => handleChange('roomType', room.type)}
                >
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="h6">{room.type}</Typography>
                      <Chip label={`$${room.price}/night`} color="primary" />
                    </Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Capacity: {room.capacity} guests
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {room.amenities}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Check-in Date"
                type="date"
                value={formData.checkIn}
                onChange={(e) => handleChange('checkIn', e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: new Date().toISOString().split('T')[0] }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Check-out Date"
                type="date"
                value={formData.checkOut}
                onChange={(e) => handleChange('checkOut', e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: formData.checkIn }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                select
                label="Number of Guests"
                value={formData.guests}
                onChange={(e) => handleChange('guests', e.target.value)}
              >
                {[1, 2, 3, 4, 5].map((num) => (
                  <MenuItem key={num} value={num}>
                    {num} {num === 1 ? 'Guest' : 'Guests'}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        )}

        {activeStep === 1 && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Guest Information
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Full Name"
                value={formData.fullName}
                onChange={(e) => handleChange('fullName', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone Number"
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="ID Number / Passport"
                value={formData.idNumber}
                onChange={(e) => handleChange('idNumber', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Special Requests"
                multiline
                rows={3}
                value={formData.specialRequests}
                onChange={(e) => handleChange('specialRequests', e.target.value)}
                placeholder="Any special requests or preferences..."
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                select
                label="Payment Method"
                value={formData.paymentMethod}
                onChange={(e) => handleChange('paymentMethod', e.target.value)}
              >
                <MenuItem value="Credit Card">Credit Card</MenuItem>
                <MenuItem value="Debit Card">Debit Card</MenuItem>
                <MenuItem value="PayPal">PayPal</MenuItem>
                <MenuItem value="Pay at Hotel">Pay at Hotel</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        )}

        {activeStep === 2 && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Booking Summary
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Card sx={{ bgcolor: '#f5f7fa' }}>
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Room Type
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {formData.roomType}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Guest Name
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {formData.fullName}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Check-in
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {format(new Date(formData.checkIn), 'MMM dd, yyyy')}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Check-out
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {format(new Date(formData.checkOut), 'MMM dd, yyyy')}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Nights
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {nights} {nights === 1 ? 'Night' : 'Nights'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Guests
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {formData.guests}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                    </Grid>
                    <Grid item xs={12}>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">Total Amount</Typography>
                        <Typography variant="h5" color="primary" fontWeight="bold">
                          ${totalAmount}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        ${selectedRoom?.price} × {nights} {nights === 1 ? 'night' : 'nights'}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2.5 }}>
        <Button onClick={handleClose}>Cancel</Button>
        {activeStep > 0 && (
          <Button onClick={handleBack}>Back</Button>
        )}
        {activeStep < steps.length - 1 ? (
          <Button variant="contained" onClick={handleNext}>
            Next
          </Button>
        ) : (
          <Button variant="contained" onClick={handleSubmit}>
            Confirm Booking
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
